import React, {
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { Option } from "react-select/src/filters";

import { Container, Icon, Link, Pressable, Row } from "../components";
import { PulseLoader } from "../components/pulse_loader";
import {
  Amenity,
  getMaxCapacity,
  LightSpace,
  OrderableType,
  PricingType,
} from "../data";
import { useGetPartnerOffering } from "../data/api";
import {
  getMeetingRoomFilterConfig,
  GalleryFilterConfig,
  PartnerOffering,
  PartnerOfferingLocation,
  SortOrder,
} from "../data/partner_offering";
import { buildPathWithParams, UrlParams, useQueryString } from "../hooks";
import { formatCurrency } from "../lib/currency/currency";
import { useI18n } from "../lib/i18n/i18n_provider";
import { groupBy, uniqueBy } from "../lib/js_utils/array_utils";
import { isEmpty } from "../lib/js_utils/lang_utils";
import {
  BookingType,
  OrderSource,
  usePartnerBookingHourQuery,
  usePartnerBookingPolicyQuery,
  usePartnerCatalogQueryMaybe,
  usePartnerOfferingFilterConfigsQuery,
  usePartnerOfferingSpacesQueryCallback,
  useSpacesQueryCallback,
} from "./api";
import { Button } from "./button";
import { Grid } from "./grid";
import { Heading } from "./heading";
import { PartnerConfig } from "./partner_config";
import { Spacer } from "./spacer";
import { FAQ } from "./template_offering_details_base";
import { MeetingRoomDateRange } from "./order_params";
import {
  Capacity,
  CapacityPicker,
  CapacityPickerMobile,
  CityPicker,
  CityPickerMobile,
  EmptyState,
  FeaturedCapacity,
  ItemCardHeader,
  ListingLocationPicker,
  ListingLocationPickerMobile,
  StatePicker,
  StatePickerMobile,
  TemplateOfferingListingLayout,
} from "./template_offering_listing_base";
import { Text } from "./text";
import { MeetingRoomDateTimePicker } from "./meeting_room_date_time_picker";
import { addMonths, format } from "date-fns";
import { getNextDayOfWeekDay } from "./date_utils";
import { DAY_FORMAT } from "./day_utils";
import { PartnerCatalog } from "../data/catalog";
import { useQuoteQuery } from "./hooks/use_quote_query";
import { PartnerBookingHour, PartnerBookingPolicy } from "../data/partners";
import { isEqual, isNil, keys } from "lodash";
import InfiniteScroll from "react-infinite-scroller";
import { Time, isAfter } from "./time_utils";
import {
  useDisplayMeetingRoomAvailableSpaces,
  usePartnerOfferingSpacesFeatureFlag,
} from "../core/splitio";
import { useIsWework } from "./hooks/use_is_wework";

interface TemplateOfferingListingMeetingRoomProps {
  showTalkWithUsPrompt?: boolean;
  filterConfig?: GalleryFilterConfig;
  partnerConfig: PartnerConfig;
  data?: {
    faqs?: FAQ[];
    title?: string;
    description?: React.ReactNode;
  };
}

interface TemplateOfferingListingMeetingRoomParams {
  offeringID: string;
}

export function TemplateOfferingListingMeetingRoom(
  props: TemplateOfferingListingMeetingRoomProps
) {
  const { filterConfig, partnerConfig, data, showTalkWithUsPrompt } = props;
  const { favicon, hubspotForm, showLanguageSelector = false } = partnerConfig;
  const { faqs, title, description } = data || {};
  const pageParams = useParams<TemplateOfferingListingMeetingRoomParams>();
  const history = useHistory();
  const searchParams = useQueryString<GalleryFilterSearchParams>();
  const [filter, setFilter] = useState<MeetingRoomsFilter>(
    deserializeMeetingRoomsFilterSearchParams(searchParams)
  );
  const partnerOffering = useGetPartnerOffering(pageParams.offeringID);
  const { data: partnerOfferingFilterConfigs } =
    usePartnerOfferingFilterConfigsQuery(partnerOffering.id);
  const meetingRoomFilterConfig = getMeetingRoomFilterConfig(
    partnerOfferingFilterConfigs
  );
  const displayMeetingRoomAvailableSpaces =
    useDisplayMeetingRoomAvailableSpaces(partnerOffering.partner.id);

  const filters = getFilterConfig(filterConfig, meetingRoomFilterConfig);
  const { data: partnerBookingPolicy } = usePartnerBookingPolicyQuery(
    partnerOffering.partner.id
  );
  const { data: partnerBookingHour } = usePartnerBookingHourQuery(
    partnerOffering.partner.id
  );
  const locations = partnerOffering.offeringLocations;
  const handleCityChange = useCallback((city?: string) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      city,
      locationID: undefined,
    }));
  }, []);

  const handleStateChange = useCallback((state?: string) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      state,
      city: undefined,
      locationID: undefined,
    }));
  }, []);

  const handleLocationChange = useCallback((locationID?: string) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      locationID,
    }));
  }, []);
  const handlePressResetFilter = useCallback(() => {
    setFilter({
      city: undefined,
      state: undefined,
      locationID: undefined,
      minCapacity: undefined,
      maxCapacity: undefined,
      date: undefined,
      startTime: undefined,
      endTime: undefined,
    });
  }, []);

  const handleCapacityChange = useCallback(
    (capacity?: { minCapacity?: number; maxCapacity?: number }) => {
      if (!capacity) {
        setFilter((prevFilter) => ({
          ...prevFilter,
          minCapacity: undefined,
          maxCapacity: undefined,
        }));
      } else {
        setFilter((prevFilter) => {
          return {
            ...prevFilter,
            minCapacity: capacity.minCapacity,
            maxCapacity: capacity.maxCapacity,
          };
        });
      }
    },
    []
  );
  const handleDateRangeChange = useCallback(
    (dateRange?: MeetingRoomDateRange) => {
      if (!dateRange) {
        setFilter((prevFilter) => ({
          ...prevFilter,
          date: undefined,
          startTime: undefined,
          endTime: undefined,
        }));
      } else {
        setFilter((prevFilter) => {
          return {
            ...prevFilter,
            date: dateRange.date,
            startTime: dateRange.startTime,
            endTime: dateRange.endTime,
          };
        });
      }
    },
    []
  );

  const handleClearDateRangeFilter = useCallback(() => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      date: undefined,
      startTime: undefined,
      endTime: undefined,
    }));
  }, []);

  useEffect(() => {
    const pathWithParams = buildPathWithParams(
      window.location.pathname,
      filter
    );
    history.replace(pathWithParams);
  }, [filter, history]);

  const states = uniqueBy(
    locations
      .map((l) => ({
        value: l.location.state,
        label: l.location.stateName,
        data: l.location,
      }))
      .sort((optionA, optionB) => optionA.label?.localeCompare(optionB.label)),
    (l) => l.value
  );

  const cities = uniqueBy(
    locations
      .map((l) => ({
        value: l.location.city as string,
        label: l.location.city as string,
        data: l.location,
      }))
      .filter(
        filter.state
          ? (l) => l.data.state === filter.state
          : (location) => location.data
      ),
    (l) => l.value
  );

  const addresses = uniqueBy(
    locations
      .map((l) => ({
        value: l.location.id as string,
        label: l.locationDisplay as string,
        data: l.location,
      }))
      .filter(
        filter.state
          ? (l) => l.data.state === filter.state
          : (location) => location.data
      )
      .filter(
        filter.city
          ? (l) => l.data.city === filter.city
          : (location) => location.data
      ),
    (l) => l.value
  );

  const hasFilter = !!(
    filter.minCapacity ||
    filter.maxCapacity ||
    filter.city ||
    filter.locationID ||
    filter.date ||
    filter.startTime ||
    filter.endTime
  );

  const displayAvailableSpaces = useCallback(() => {
    if (!displayMeetingRoomAvailableSpaces) {
      return false;
    }
    // When endTime does not set - only show actual available spaces
    if (!filter.endTime) {
      return false;
    }
    // When user select endTime after 1730 - show additional space
    const START_TIME_TO_SHOW_AVAILABLE_SPACES = "1730" as Time;
    if (isAfter(filter.endTime as Time, START_TIME_TO_SHOW_AVAILABLE_SPACES)) {
      return true;
    }

    return false;
  }, [displayMeetingRoomAvailableSpaces, filter.endTime]);

  return (
    <TemplateOfferingListingLayout
      title={partnerOffering.offeringTitle || title || ""}
      description={description}
      favicon={favicon}
      showLanguageSelector={showLanguageSelector}
      partnerConfig={partnerConfig}
      showTalkWithUsPrompt={showTalkWithUsPrompt}
      hubspotForm={hubspotForm}
      offeringID={pageParams.offeringID}
      faqs={faqs}
      partnerOffering={partnerOffering}
      onLocaleSwitch={handlePressResetFilter}
      desktopFilters={
        <DesktopFilters
          filterConfig={filters}
          onCapacityChange={handleCapacityChange}
          onCityChange={handleCityChange}
          onStateChange={handleStateChange}
          onLocationChange={handleLocationChange}
          onChangeDateRange={handleDateRangeChange}
          filter={filter}
          cities={cities}
          states={states}
          streetAddresses={addresses}
          partnerID={partnerOffering.partner.id}
          partnerBookingPolicy={partnerBookingPolicy}
          partnerBookingHour={partnerBookingHour}
          onClearDateRangeFilter={handleClearDateRangeFilter}
        />
      }
      mobileFilters={
        <MobileFilters
          filterConfig={filters}
          cities={cities}
          states={states}
          onFilterChange={setFilter}
          filter={filter}
          locations={locations}
          hasFilter={hasFilter}
          partnerID={partnerOffering.partner.id}
          partnerBookingPolicy={partnerBookingPolicy}
          partnerBookingHour={partnerBookingHour}
        />
      }
      listingContent={
        <Container>
          <Suspense fallback={<PulseLoader />}>
            <MeetingRoomsGallery
              hasFilter={hasFilter}
              partnerOfferingID={pageParams.offeringID}
              partnerOffering={partnerOffering}
              filter={filter}
              onPressResetFilter={handlePressResetFilter}
              sortOrder={filters?.defaultSortOrder}
              featuredCapacity={partnerConfig.featuredCapacity}
            />
          </Suspense>
        </Container>
      }
      availableSpaces={
        displayAvailableSpaces() && (
          <AvailableSpaces
            partnerOffering={partnerOffering}
            availableDate={filter.date}
            partnerOfferingID={pageParams.offeringID}
            featuredCapacity={partnerConfig.featuredCapacity}
            sortOrder={filters?.defaultSortOrder}
          />
        )
      }
    />
  );
}

interface AvailableSpacesProps {
  partnerOfferingID: string;
  partnerOffering: PartnerOffering;
  availableDate?: string;
  sortOrder?: SortOrder;
  featuredCapacity?: FeaturedCapacity;
}

function AvailableSpaces(props: AvailableSpacesProps) {
  const {
    partnerOfferingID,
    partnerOffering,
    sortOrder,
    featuredCapacity,
    availableDate,
  } = props;
  const { fbt } = useI18n();
  const filter: MeetingRoomsFilter = { date: availableDate };

  return (
    <Container>
      <Suspense fallback={<PulseLoader />}>
        <h3>{fbt("Available spaces")}</h3>
        <Text>
          {fbt(
            "Here are some additional spaces you might be interested in, available during our working hours 09:00-17:30"
          )}
        </Text>
        <Spacer size={50} />
        <MeetingRoomsGallery
          partnerOfferingID={partnerOfferingID}
          partnerOffering={partnerOffering}
          sortOrder={sortOrder}
          featuredCapacity={featuredCapacity}
          filter={filter}
          hasFilter={true}
        />
      </Suspense>
    </Container>
  );
}

interface MeetingRoomsGalleryProps {
  filter: MeetingRoomsFilter;
  partnerOffering: PartnerOffering;
  partnerOfferingID: string;
  hasFilter: boolean;
  sortOrder?: SortOrder;
  featuredCapacity?: FeaturedCapacity;
  onPressResetFilter?: () => void;
}

const CITY_PER_PAGE = 2;

interface CitySpace {
  city: string;
  spaces: LightSpace[];
}

function MeetingRoomsGallery(props: MeetingRoomsGalleryProps) {
  const {
    filter,
    hasFilter,
    partnerOffering,
    partnerOfferingID,
    featuredCapacity,
    onPressResetFilter,
    sortOrder,
  } = props;
  const partnerOfferingSpacesFeatureFlag =
    usePartnerOfferingSpacesFeatureFlag();
  const [hasMore, setHasMore] = useState(false);
  const getSpaces = useSpacesQueryCallback();
  // TODO clean this when we fully migrate to usePartnerOfferingSpacesQuery
  const partnerOfferingWithFilter = useGetPartnerOffering(
    partnerOfferingID,
    partnerOfferingSpacesFeatureFlag ? undefined : filter
  );
  const getPartnerOfferingSpaces = usePartnerOfferingSpacesQueryCallback();
  const [citySpaces, setCitySpaces] = useState<CitySpace[]>([]);
  const [loading, setLoading] = useState(true);
  // This ref is used to prevent duplicate fetching the same data.
  // Because the component rerender many times at the beginning
  const fetchingCitiesSpace = useRef<string[]>();

  const offeringLocationsGroupedByCity = useMemo(
    () =>
      groupBy(
        partnerOfferingSpacesFeatureFlag
          ? partnerOffering.offeringLocations
          : partnerOfferingWithFilter.offeringLocations,
        (l) => l.location.city || ""
      ),
    [
      partnerOfferingWithFilter,
      partnerOffering,
      partnerOfferingSpacesFeatureFlag,
    ]
  );

  const cities = useMemo(
    () => keys(offeringLocationsGroupedByCity),
    [offeringLocationsGroupedByCity]
  );

  const fetchMoreData = useCallback(async () => {
    setLoading(true);
    if (citySpaces.length === cities.length && cities.length !== 0) {
      setHasMore(false);
      setLoading(false);
      return;
    }

    const start = citySpaces.length;
    const end = Math.min(citySpaces.length + CITY_PER_PAGE, cities.length);
    const fetchingCities =
      filter.city && partnerOfferingSpacesFeatureFlag
        ? [filter.city]
        : cities.slice(start, end);
    if (isEqual(fetchingCitiesSpace.current, fetchingCities)) {
      return;
    }

    fetchingCitiesSpace.current = fetchingCities;
    const data = await Promise.all(
      fetchingCities.map(async (city) => {
        if (partnerOfferingSpacesFeatureFlag) {
          const spaces = await getPartnerOfferingSpaces(
            partnerOfferingID,
            city,
            filter
          );

          return {
            city,
            spaces,
          };
        } else {
          const cityLocations = offeringLocationsGroupedByCity[city];
          const spaceIDs = cityLocations.map((l) => l.spaceID);
          const spaces = await getSpaces(spaceIDs);

          return {
            city,
            spaces,
          };
        }
      })
    );

    if (isEqual(fetchingCitiesSpace.current, fetchingCities)) {
      fetchingCitiesSpace.current = [];
      if (filter.city && partnerOfferingSpacesFeatureFlag) {
        setCitySpaces([...data]);
        setHasMore(false);
      } else {
        setCitySpaces([...citySpaces, ...data]);
      }
    }
    setLoading(false);
  }, [
    citySpaces,
    cities,
    setHasMore,
    setCitySpaces,
    getPartnerOfferingSpaces,
    offeringLocationsGroupedByCity,
    filter,
    getSpaces,
    partnerOfferingID,
    partnerOfferingSpacesFeatureFlag,
  ]);

  useEffect(() => {
    fetchingCitiesSpace.current = [];
    setCitySpaces([]);
    setHasMore(true);
  }, [filter, setHasMore, setCitySpaces]);

  const hasAvailableSpace = useCallback(() => {
    if (partnerOfferingSpacesFeatureFlag) {
      return citySpaces.some((citySpace) => {
        return citySpace.spaces.length > 0;
      });
    } else {
      return false;
    }
  }, [citySpaces, partnerOfferingSpacesFeatureFlag]);

  return (
    <InfiniteScroll
      loadMore={fetchMoreData}
      hasMore={hasMore}
      loader={<PulseLoader />}
    >
      {(isEmpty(partnerOfferingWithFilter.offeringLocations) ||
        !hasAvailableSpace()) &&
      !loading &&
      onPressResetFilter ? (
        <EmptyState
          hasFilter={hasFilter}
          onPressResetFilter={onPressResetFilter}
        />
      ) : (
        citySpaces
          .filter(
            ({ city, spaces }) => !isNil(offeringLocationsGroupedByCity[city])
          )
          .map(({ city, spaces }) => {
            const offeringLocations = offeringLocationsGroupedByCity[city];
            if (partnerOfferingSpacesFeatureFlag && spaces.length === 0) {
              return <></>;
            }
            return (
              <div key={city}>
                <MeetingRoomGalleryItemList
                  key={city}
                  spaces={spaces}
                  partnerOfferingLocations={offeringLocations}
                  city={city}
                  partnerOffering={
                    partnerOfferingSpacesFeatureFlag
                      ? partnerOfferingWithFilter
                      : partnerOffering
                  }
                  partnerOfferingID={partnerOfferingID}
                  sortOrder={sortOrder}
                  filter={filter}
                  featuredCapacity={featuredCapacity}
                />
              </div>
            );
          })
      )}
    </InfiniteScroll>
  );
}

interface MeetingRoomGalleryItemListProps {
  city: string;
  partnerOfferingID: string;
  partnerOffering: PartnerOffering;
  partnerOfferingLocations: PartnerOfferingLocation[];
  sortOrder?: SortOrder;
  filter?: MeetingRoomsFilter;
  featuredCapacity?: FeaturedCapacity;
  spaces: LightSpace[];
}

function MeetingRoomGalleryItemList(props: MeetingRoomGalleryItemListProps) {
  const {
    city,
    partnerOfferingID,
    partnerOffering,
    sortOrder,
    partnerOfferingLocations,
    featuredCapacity,
    filter,
    spaces,
  } = props;
  const featuredSpaces = featuredCapacity
    ? spaces
        .filter(
          (s) =>
            s.layouts[0].capacity <= featuredCapacity?.maxCapacity &&
            s.layouts[0].capacity >= featuredCapacity?.minCapacity
        )
        .sort((s1, s2) => s1.layouts[0].capacity - s2.layouts[0].capacity)
    : [];
  const otherSpaces = featuredCapacity
    ? spaces.filter((s) => featuredSpaces.indexOf(s) === -1)
    : spaces;

  // featured spaces (with capacity configuration) will be displayed first,
  // and then the rest of spaces, ordering by its capacity by default
  // unless they have the same capacity then ordering by their names
  const willDisplaySpaces = featuredSpaces.concat(
    otherSpaces.sort((spaceA, spaceB) => {
      const capacitySpaceA = spaceA.layouts[0].capacity;
      const capacitySpaceB = spaceB.layouts[0].capacity;
      if (capacitySpaceA !== capacitySpaceB) {
        if (sortOrder && sortOrder === SortOrder.HighestToLowest) {
          return capacitySpaceB - capacitySpaceA;
        }
        return capacitySpaceA - capacitySpaceB;
      }
      return spaceA.name.localeCompare(spaceB.name);
    })
  );

  return (
    <Container>
      <Heading>{city}</Heading>
      <Spacer size={16} />
      <Grid itemsPerRow={{ md: 3, default: 1 }} spacing={16}>
        {willDisplaySpaces.map((space) => (
          <MeetingRoomGroup
            key={space.id}
            space={space}
            partnerOffering={partnerOffering}
            partnerOfferingID={partnerOfferingID}
            partnerOfferingLocations={partnerOfferingLocations}
            filter={filter}
          />
        ))}
      </Grid>
      <Spacer size={24} />
    </Container>
  );
}

interface MeetingRoomGroupProps {
  space: LightSpace;
  partnerOfferingID: string;
  partnerOffering: PartnerOffering;
  partnerOfferingLocations: PartnerOfferingLocation[];
  filter?: MeetingRoomsFilter;
}

function MeetingRoomGroup(props: MeetingRoomGroupProps) {
  const {
    space,
    partnerOfferingID,
    partnerOffering,
    partnerOfferingLocations,
    filter,
  } = props;
  const fromCatalog = filter?.fromCatalog;

  const partnerCatalog = usePartnerCatalogQueryMaybe(
    partnerOffering.partner.partnerSlug,
    fromCatalog
  );
  const featuredPromoDescription = function (spaceID: string) {
    const spaceOfferingLocation = partnerOfferingLocations.find(
      (p) => p.spaceID === spaceID
    );
    return spaceOfferingLocation?.featuredPromoDescription;
  };
  const offeringDetailsUrl = buildPathWithParams(
    `/offerings/${partnerOfferingID}/spaces/${space.id}`,
    filter
  );

  return (
    <Container key={space.id} height="100%" paddingBottom={16}>
      <Link
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        to={offeringDetailsUrl}
      >
        <Container
          flex={1}
          borderWidth={1}
          borderRadius={5}
          borderColor="#D7D5DD"
        >
          <Suspense
            fallback={
              <Container minHeight={240} center>
                <PulseLoader />
              </Container>
            }
          >
            <MeetingRoomGalleryItem
              space={space}
              featuredPromoDescription={featuredPromoDescription(space.id)}
              partnerCatalog={partnerCatalog}
              partnerID={partnerOffering.partner.id}
            />
          </Suspense>
        </Container>
      </Link>
    </Container>
  );
}

function getOptimisticallyAvailableDate() {
  const fourMonthsFromToday = addMonths(new Date(), 4);
  const wednesday = getNextDayOfWeekDay(fourMonthsFromToday, 3);
  return format(wednesday, DAY_FORMAT);
}

interface MeetingRoomGalleryItemProps {
  space: LightSpace;
  featuredPromoDescription?: string;
  /* Additional params support manage navigation from catalog page */
  partnerCatalog?: PartnerCatalog;
  partnerID: string;
}

function MeetingRoomGalleryItem(props: MeetingRoomGalleryItemProps) {
  const { space, featuredPromoDescription, partnerCatalog, partnerID } = props;
  const OPTIMISTIC_AVAILABLE_START_TIME = "1200";
  const OPTIMISTIC_AVAILABLE_END_TIME = "1300";
  /**
   * Special quote payload to obtain & display fixed price/ discounted price per hour for a given meeting room
   * (given accessed from catalog page that is associated with a promo code)
   */
  const quoteInput = useMemo(
    () => ({
      source: "WL" as OrderSource,
      partnerID: partnerID,
      promoCode: partnerCatalog?.promoCode || "",
      orderItems: [
        {
          type: OrderableType.Booking as const,
          quantity: undefined,
          details: {
            type: BookingType.Hourly as const,
            spaceID: space.id,
            date: getOptimisticallyAvailableDate(),
            startTime: OPTIMISTIC_AVAILABLE_START_TIME,
            endTime: OPTIMISTIC_AVAILABLE_END_TIME,
            legacy__details: {
              attendees: 1,
              layoutID: "",
            },
          },
        },
      ],
    }),
    [partnerCatalog, partnerID, space.id]
  );
  const { data: quote } = useQuoteQuery(
    partnerCatalog?.promoCode ? quoteInput : null
  );

  const hourlyPrice = space.pricings.find(
    (price) => price.type === PricingType.Hourly
  );

  const price =
    quote && quote.promotionLine
      ? quote.promotionLine.itemPriceAfterDiscount
      : hourlyPrice?.price;

  return (
    <Container flex={1}>
      <ItemCardHeader
        coverImage={{ src: space.images[0].small.url, alt: space.name }}
        title={space.name}
        subTitle={space.streetAddress}
        featuredPromoDescription={featuredPromoDescription}
      />
      <Spacer />
      <Spacer size={24} />
      <Container padding={8}>
        <Row alignItems="center" justifyContent="space-between">
          <Capacity capacity={getMaxCapacity(space)} />
          {!!price && (
            <MeetingRoomPricing currency={space.currencyCode} price={price} />
          )}
        </Row>
      </Container>
    </Container>
  );
}

interface MeetingRoomPricingProps {
  price: number;
  currency: string | null;
}

export function MeetingRoomPricing(props: MeetingRoomPricingProps) {
  const { price, currency } = props;
  const { fbt, locale } = useI18n();
  const isWeWork = useIsWework();
  return (
    <Row alignItems="center">
      <Text weight="bold">
        {formatCurrency(price, currency, locale.languageTag)}
      </Text>
      <Spacer size={4} direction="horizontal" />
      <Text>{isWeWork ? ` ${fbt("before VAT per hour")}` : fbt("/hr")}</Text>
    </Row>
  );
}

interface AmenitiesProps {
  amenities: Amenity[];
}

export function Amenities(props: AmenitiesProps) {
  const { amenities } = props;

  return (
    <Row wrap>
      {amenities.map((amenity) => (
        <React.Fragment key={amenity.name}>
          <img
            style={{ width: 24, height: 24 }}
            src={amenity.iconURL}
            alt={amenity.name}
          />
          <Spacer size={8} direction="horizontal" />
        </React.Fragment>
      ))}
    </Row>
  );
}

interface DesktopFiltersProps {
  filterConfig: GalleryFilterConfig;
  onCityChange: (city?: string) => void;
  onStateChange: (state?: string) => void;
  onLocationChange: (locationID?: string) => void;
  onCapacityChange: (capacity?: {
    minCapacity?: number;
    maxCapacity?: number;
  }) => void;
  onChangeDateRange: (dateRange?: MeetingRoomDateRange) => void;
  onClearDateRangeFilter: () => void;
  filter: MeetingRoomsFilter;
  partnerBookingPolicy?: PartnerBookingPolicy;
  partnerBookingHour?: PartnerBookingHour;
  cities: Option[];
  states: Option[];
  streetAddresses: Option[];
  partnerID: string;
}

export function DesktopFilters(props: DesktopFiltersProps) {
  const {
    filterConfig,
    filter,
    cities,
    states,
    streetAddresses,
    onCapacityChange,
    onCityChange,
    onStateChange,
    onLocationChange,
    onChangeDateRange,
    onClearDateRangeFilter,
    partnerBookingPolicy,
    partnerBookingHour,
  } = props;

  return (
    <div className="mr-desktop-filters-container">
      <Row>
        {filterConfig.state && (
          <Fragment>
            <Container
              maxWidth={!!filterConfig?.datetime ? 170 : 200}
              flex={1}
              className="state-picker"
            >
              <StatePicker
                onChange={onStateChange}
                states={states}
                value={filter.state}
              />
            </Container>
            <Spacer size={10} direction="horizontal" />
          </Fragment>
        )}
        {filterConfig.city && (
          <Fragment>
            <Container
              maxWidth={!!filterConfig?.datetime ? 150 : 200}
              flex={1}
              className="city-picker"
            >
              <CityPicker
                onChange={onCityChange}
                cities={cities}
                value={filter.city}
              />
            </Container>
            <Spacer size={10} direction="horizontal" />
          </Fragment>
        )}
        {filterConfig.location && (
          <Fragment>
            <Container maxWidth={300} flex={1}>
              <ListingLocationPicker
                onChange={onLocationChange}
                locations={streetAddresses}
                value={filter.locationID}
              />
            </Container>
            <Spacer size={10} direction="horizontal" />
          </Fragment>
        )}
        {filterConfig.capacity && (
          <Fragment>
            <Container maxWidth={200} flex={1}>
              <CapacityPicker
                onChange={onCapacityChange}
                value={filter}
                capacityOptions={filterConfig.capacityOptions}
              />
            </Container>
            <Spacer size={10} direction="horizontal" />
          </Fragment>
        )}
        {!!filterConfig?.datetime && (
          <Fragment>
            <Container maxWidth={300} flex={1}>
              <MeetingRoomDateTimePicker
                onChangeDateRange={onChangeDateRange}
                onClearFilter={onClearDateRangeFilter}
                view="dialog"
                dateRangeValue={{
                  date: filter.date,
                  startTime: filter.startTime,
                  endTime: filter.endTime,
                }}
                minStartTime={partnerBookingHour?.openAt}
                maxEndTime={partnerBookingHour?.closedAt}
                timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
              />
            </Container>
            <Spacer size={10} direction="horizontal" />
          </Fragment>
        )}
      </Row>
    </div>
  );
}

interface MobileFiltersProps {
  filterConfig?: GalleryFilterConfig;
  onFilterChange: (filter: MeetingRoomsFilter) => void;
  filter: MeetingRoomsFilter;
  cities: Option[];
  states: Option[];
  hasFilter: boolean;
  locations: PartnerOfferingLocation[];
  partnerID: string;
  partnerBookingPolicy?: PartnerBookingPolicy;
  partnerBookingHour?: PartnerBookingHour;
}

export function MobileFilters(props: MobileFiltersProps) {
  const {
    filterConfig,
    cities,
    states,
    hasFilter,
    locations,
    filter,
    onFilterChange,
    partnerBookingPolicy,
    partnerBookingHour,
  } = props;
  const [localFilter, setLocalFilter] = useState(filter);
  const [advanced, setAdvanced] = useState(false);
  const { fbt } = useI18n();
  const handleCityChange = useCallback(
    (city?: string) => {
      setLocalFilter((prevFilter) => ({
        ...prevFilter,
        city,
        locationID: undefined,
      }));
      if (!advanced) {
        onFilterChange({
          ...localFilter,
          city,
          locationID: undefined,
        });
      }
    },
    [localFilter, onFilterChange, advanced]
  );
  const handleStateChange = useCallback(
    (state?: string) => {
      setLocalFilter((prevFilter) => ({
        ...prevFilter,
        state,
        locationID: undefined,
      }));
      if (!advanced) {
        onFilterChange({
          ...localFilter,
          state,
          locationID: undefined,
        });
      }
    },
    [localFilter, onFilterChange, advanced]
  );

  const addresses = uniqueBy(
    locations
      .map((l) => ({
        value: l.location.id as string,
        label: l.locationDisplay as string,
        data: l.location,
      }))
      .filter(
        localFilter.city
          ? (l) => l.data.city === localFilter.city
          : (location) => location.data
      ),
    (l) => l.value
  );

  const handleLocationChange = useCallback(
    (locationID?: string) => {
      setLocalFilter((prevFilter) => ({
        ...prevFilter,
        locationID,
      }));

      if (!advanced) {
        onFilterChange({
          ...localFilter,
          locationID,
        });
      }
    },
    [localFilter, onFilterChange, advanced]
  );

  const handleCapacityChange = useCallback(
    (capacity?: { minCapacity?: number; maxCapacity?: number }) => {
      if (!capacity) {
        setLocalFilter((prevFilter) => ({
          ...prevFilter,
          minCapacity: undefined,
          maxCapacity: undefined,
        }));
      } else {
        setLocalFilter((prevFilter) => ({
          ...prevFilter,
          minCapacity: capacity.minCapacity,
          maxCapacity: capacity.maxCapacity,
        }));
      }

      if (!advanced) {
        onFilterChange({
          ...localFilter,
          minCapacity: capacity?.minCapacity,
          maxCapacity: capacity?.maxCapacity,
        });
      }
    },
    [localFilter, onFilterChange, advanced]
  );

  const handleDateRangeChange = useCallback(
    (dateRange?: MeetingRoomDateRange) => {
      if (!dateRange) {
        setLocalFilter((prevFilter) => ({
          ...prevFilter,
          date: undefined,
          startTime: undefined,
          endTime: undefined,
        }));
      } else {
        setLocalFilter((prevFilter) => {
          return {
            ...prevFilter,
            date: dateRange.date,
            startTime: dateRange.startTime,
            endTime: dateRange.endTime,
          };
        });
      }

      onFilterChange({
        ...localFilter,
        date: dateRange?.date,
        startTime: dateRange?.startTime,
        endTime: dateRange?.endTime,
      });
    },
    [localFilter, onFilterChange]
  );

  const handleClearDateRangeFilter = useCallback(() => {
    setLocalFilter((prevFilter) => ({
      ...prevFilter,
      date: undefined,
      startTime: undefined,
      endTime: undefined,
    }));

    if (!advanced) {
      onFilterChange({
        ...localFilter,
        date: undefined,
        startTime: undefined,
        endTime: undefined,
      });
    }
  }, [advanced, localFilter, onFilterChange]);

  const handleSubmitFilter = useCallback(() => {
    setAdvanced(false);
    onFilterChange(localFilter);
  }, [localFilter, onFilterChange]);

  if (!advanced) {
    return (
      <Container
        backgroundColor="white"
        boxShadow="0px 2px 7px 3px rgba(0, 0, 0, 0.25)"
        padding={16}
      >
        <Row>
          <Container flex={1}>
            {filterConfig?.state ? (
              <StatePickerMobile
                onChange={handleStateChange}
                states={states}
                value={filter.state}
              />
            ) : filterConfig?.city ? (
              <CityPickerMobile
                onChange={handleCityChange}
                cities={cities}
                value={filter.city}
              />
            ) : filterConfig?.capacity ? (
              <CapacityPickerMobile
                onChange={handleCapacityChange}
                value={{
                  minCapacity: filter.minCapacity,
                  maxCapacity: filter.maxCapacity,
                }}
                capacityOptions={filterConfig.capacityOptions}
              />
            ) : (
              <ListingLocationPickerMobile
                onChange={handleLocationChange}
                locations={addresses}
                value={localFilter.locationID}
              />
            )}
          </Container>
          <Spacer size={16} direction="horizontal" />
          <Container center>
            <Pressable onPress={() => setAdvanced(true)}>
              <Icon name="filter" />
            </Pressable>
            {hasFilter && (
              <div
                style={{
                  position: "absolute",
                  width: 8,
                  height: 8,
                  backgroundColor: "#000",
                  top: 0,
                  right: 0,
                  borderRadius: 999,
                }}
              />
            )}
          </Container>
        </Row>
      </Container>
    );
  }

  return (
    <Container
      backgroundColor="white"
      boxShadow="0px 2px 7px 3px rgba(0, 0, 0, 0.25)"
      padding={16}
    >
      <Container>
        <Pressable
          style={{ position: "absolute", left: 4 }}
          onPress={() => setAdvanced(false)}
        >
          <Icon name="x" />
        </Pressable>
        <Heading align="center">{fbt("Filters")}</Heading>
      </Container>
      <Spacer size={16} />
      {filterConfig?.state && (
        <Fragment>
          <Container>
            <StatePickerMobile
              onChange={handleStateChange}
              states={states}
              value={localFilter.state}
            />
          </Container>
          <Spacer size={8} />
        </Fragment>
      )}
      {filterConfig?.city && (
        <Fragment>
          <Container>
            <CityPickerMobile
              onChange={handleCityChange}
              cities={cities}
              value={localFilter.city}
            />
          </Container>
          <Spacer size={8} />
        </Fragment>
      )}
      {filterConfig?.location && (
        <Fragment>
          <Container>
            <ListingLocationPickerMobile
              onChange={handleLocationChange}
              locations={addresses}
              value={localFilter.locationID}
            />
          </Container>
          <Spacer size={8} />
        </Fragment>
      )}
      {filterConfig?.capacity && (
        <Container>
          <CapacityPickerMobile
            onChange={handleCapacityChange}
            value={localFilter}
            capacityOptions={filterConfig.capacityOptions}
          />
          <Spacer size={8} />
        </Container>
      )}
      {!!filterConfig?.datetime && (
        <Container>
          <MeetingRoomDateTimePicker
            onChangeDateRange={handleDateRangeChange}
            onClearFilter={handleClearDateRangeFilter}
            view="modal"
            dateRangeValue={{
              date: filter.date,
              startTime: filter.startTime,
              endTime: filter.endTime,
            }}
            minStartTime={partnerBookingHour?.openAt}
            maxEndTime={partnerBookingHour?.closedAt}
            timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
          />
        </Container>
      )}
      <Spacer size={16} />
      <Button onPress={handleSubmitFilter} title={fbt("Filter")} />
    </Container>
  );
}

export interface GalleryFilterSearchParams {
  state?: string;
  city?: string;
  cities?: string;
  locationID?: string;
  locationIDs?: string;
  minCapacity?: string;
  maxCapacity?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  startDate?: string;
  endDate?: string;
  layoutId?: string;
  /* Additional params support manage navigation from catalog page */
  fromCatalog?: string;
}

export interface MeetingRoomsFilter extends UrlParams {
  city?: string;
  cities?: string[];
  locationID?: string;
  locationIDs?: string[];
  minCapacity?: number;
  maxCapacity?: number;
  date?: string;
  startTime?: string;
  endTime?: string;
  startDate?: string;
  endDate?: string;
  state?: string;
  layoutId?: string;
  /* Additional params support manage navigation from catalog page */
  fromCatalog?: string;
}

export function deserializeMeetingRoomsFilterSearchParams(
  searchParams?: GalleryFilterSearchParams
): MeetingRoomsFilter {
  const dateTimeParams = searchParams?.date
    ? {
        date: searchParams?.date,
        startTime: searchParams?.startTime,
        endTime: searchParams?.endTime,
      }
    : searchParams?.startDate && searchParams.endDate
    ? {
        startDate: searchParams?.startDate,
        endDate: searchParams?.endDate,
      }
    : undefined;
  return {
    city: searchParams?.city,
    cities: searchParams?.cities?.split(","),
    state: searchParams?.state,
    locationID: searchParams?.locationID,
    locationIDs: searchParams?.locationIDs?.split(","),
    minCapacity: searchParams?.minCapacity
      ? Number(searchParams?.minCapacity)
      : undefined,
    maxCapacity: searchParams?.maxCapacity
      ? Number(searchParams?.maxCapacity)
      : undefined,
    fromCatalog: searchParams?.fromCatalog,
    layoutId: searchParams?.layoutId,
    ...dateTimeParams,
  };
}

export function getFilterConfig(
  filterConfig?: GalleryFilterConfig,
  meetingRoomFilterConfig?: GalleryFilterConfig
) {
  return {
    city: meetingRoomFilterConfig
      ? meetingRoomFilterConfig.city
      : filterConfig?.city,
    location: meetingRoomFilterConfig
      ? meetingRoomFilterConfig.location
      : filterConfig?.location,
    capacity: meetingRoomFilterConfig
      ? meetingRoomFilterConfig.capacity
      : filterConfig?.capacity,
    datetime: meetingRoomFilterConfig
      ? meetingRoomFilterConfig.datetime
      : filterConfig?.datetime,
    state: meetingRoomFilterConfig
      ? meetingRoomFilterConfig.state
      : filterConfig?.state,
    layout: meetingRoomFilterConfig
      ? meetingRoomFilterConfig.layout
      : filterConfig?.layout,
    capacityOptions: filterConfig?.capacityOptions,
    defaultSortOrder: filterConfig?.defaultSortOrder,
  };
}
